import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Server,
  RefreshCw,
  TrendingUp,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api/client'

// Types
interface HealthStats {
  api_performance: {
    success_rate: number
    avg_response_ms: number
    total_requests_24h: number
    failed_requests_24h: number
    status: string
  }
  tool_errors: Array<{
    tool: string
    calls: number
    errors: number
    rate: number
  }>
  services: Record<
    string,
    {
      status: string
      last_activity: string
    }
  >
  error_trend_7d: Array<{
    date: string
    errors: number
  }>
}

// API function
const fetchHealthStats = async (): Promise<HealthStats> => {
  const response = await apiClient.get('/admin/stats/health')
  return response.data
}

// Status indicator component
const StatusIndicator = ({
  status,
  successRate,
  responseMs,
}: {
  status?: string
  successRate?: number
  responseMs?: number
}) => {
  let color = 'bg-green-500'
  let icon = CheckCircle2
  let label = 'Healthy'

  if (status === 'inactive' || status === 'error') {
    color = 'bg-red-500'
    icon = XCircle
    label = 'Error'
  } else if (status === 'degraded') {
    color = 'bg-yellow-500'
    icon = AlertTriangle
    label = 'Degraded'
  } else if (successRate !== undefined && responseMs !== undefined) {
    if (successRate < 95 || responseMs > 500) {
      color = 'bg-red-500'
      icon = XCircle
      label = 'Critical'
    } else if (successRate < 99 || responseMs > 200) {
      color = 'bg-yellow-500'
      icon = AlertTriangle
      label = 'Warning'
    }
  }

  const Icon = icon
  return (
    <div className="flex items-center gap-2">
      <div className={`h-3 w-3 rounded-full ${color}`} />
      <Icon className={`h-4 w-4 ${color.replace('bg-', 'text-')}`} />
      <span className="text-sm">{label}</span>
    </div>
  )
}

// Service status card
const ServiceCard = ({
  name,
  status,
  lastActivity,
}: {
  name: string
  status: string
  lastActivity: string
}) => {
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  const statusColor =
    status === 'active' ? 'bg-green-500' : status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'

  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`h-3 w-3 rounded-full ${statusColor}`} />
        <div>
          <div className="font-medium text-sm">{name}</div>
          <div className="text-xs text-muted-foreground">
            Last activity: {formatTime(lastActivity)}
          </div>
        </div>
      </div>
      <Badge variant={status === 'active' ? 'default' : 'destructive'}>{status}</Badge>
    </div>
  )
}

// Simple line chart for error trend
const ErrorTrendChart = ({ data }: { data: Array<{ date: string; errors: number }> }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-sm text-muted-foreground py-4">
        No error trend data available
      </div>
    )
  }

  const maxErrors = Math.max(...data.map((d) => d.errors), 1)
  const height = 80

  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between h-20 gap-1">
        {data.map((day, i) => {
          const barHeight = (day.errors / maxErrors) * height
          return (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div
                className={`w-full rounded-t ${day.errors > 0 ? 'bg-red-400' : 'bg-green-400'}`}
                style={{ height: `${Math.max(barHeight, 4)}px` }}
                title={`${day.date}: ${day.errors} errors`}
              />
            </div>
          )
        })}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{data[0]?.date.slice(5)}</span>
        <span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  )
}

export function SystemHealthTab() {
  const {
    data: health,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin-health-stats'],
    queryFn: fetchHealthStats,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !health) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <p>Failed to load health statistics</p>
        <button onClick={() => refetch()} className="text-primary underline mt-2">
          Try again
        </button>
      </div>
    )
  }

  const serviceNames: Record<string, string> = {
    central_api: 'Central API',
    mcp_server: 'MCP Server',
    lca_workers: 'LCA Workers',
    chat_service: 'Chat Service',
  }

  return (
    <div className="space-y-6">
      {/* API Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            API Performance (24h)
          </CardTitle>
          <CardDescription>Overall system performance metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-5">
            <div className="col-span-1">
              <StatusIndicator
                successRate={health.api_performance.success_rate}
                responseMs={health.api_performance.avg_response_ms}
              />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {health.api_performance.success_rate.toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground">Success Rate</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{health.api_performance.avg_response_ms}ms</div>
              <div className="text-xs text-muted-foreground">Avg Response</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{health.api_performance.total_requests_24h}</div>
              <div className="text-xs text-muted-foreground">Total Requests</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {health.api_performance.failed_requests_24h}
              </div>
              <div className="text-xs text-muted-foreground">Failed Requests</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Service Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Service Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(health.services).map(([key, service]) => (
              <ServiceCard
                key={key}
                name={serviceNames[key] || key}
                status={service.status}
                lastActivity={service.last_activity}
              />
            ))}
          </CardContent>
        </Card>

        {/* Error Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Error Trend (7 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ErrorTrendChart data={health.error_trend_7d} />
            <div className="mt-4 text-center text-sm text-muted-foreground">
              Total errors: {health.error_trend_7d.reduce((sum, d) => sum + d.errors, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tool Errors Table */}
      {health.tool_errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Tool Error Breakdown
            </CardTitle>
            <CardDescription>Tools with errors in the last 24 hours</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tool</TableHead>
                  <TableHead className="text-right">Total Calls</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Error Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {health.tool_errors.map((tool) => (
                  <TableRow key={tool.tool}>
                    <TableCell className="font-mono text-sm">{tool.tool}</TableCell>
                    <TableCell className="text-right">{tool.calls}</TableCell>
                    <TableCell className="text-right text-red-600">{tool.errors}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={tool.rate > 5 ? 'destructive' : 'secondary'}>
                        {tool.rate.toFixed(1)}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {health.tool_errors.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-2" />
            <p className="text-muted-foreground">No tool errors in the last 24 hours</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
